import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './insights.scss';

import connect from '@app/utils/connect';

import getGroupByOptions from '@app/insights/getGroupByOptions';
import getSelectedGroupByOptions from '@app/utils/getSelectedGroupByOptions';
import getInsightsColumns from '@app/utils/insightsColumns';
import parseStoredGroupBy from '@app/utils/parseStoredGroupBy';
import orderMap from '@app/insights/orderByMap';

import {
    MAX_INSIGHTS_ROWS,
    METRIC_TYPES,
    SORTING_ORDER,
    getYAxisMap,
} from '@app/constants/insights';

import { BROADCAST, IN_VENUE } from '@app/constants/platformIds';
import useInsights from '@app/components/hooks/useInsights';
import useCompareInsights from '@app/components/hooks/useCompareInsights';
import { useRightsholderAttribution } from '@app/components/hooks/useRightsholderAttribution';
import { getSettings } from '@app/insights/utils';
import { Client, User } from '@app/types/api';
import { useLocation } from 'react-router-dom';
import { EDGE_RIGHTSHOLDER_ATTRIBUTION } from '@app/constants/config';

export const InsightsContext = React.createContext<InsightsContextType>(null);

type ProviderProps = {
    children: React.ReactNode;
    user: User;
    client: Client;
    filters: any;
};

type ColumnSettings = Record<string, boolean>;

type InsightsContextType = {
    yAxis: string;
    setYAxis: React.Dispatch<React.SetStateAction<string>>;
    compareMetric: string;
    setCompareMetric: React.Dispatch<React.SetStateAction<string>>;
    page: number;
    setPage: React.Dispatch<React.SetStateAction<number>>;
    groupByOptions: any[];
    setGroupByOptions: React.Dispatch<React.SetStateAction<any[]>>;
    columns: ColumnSettings;
    setColumns: React.Dispatch<React.SetStateAction<ColumnSettings>>;
    insightsColumns: any[];
    initialColumns: ColumnSettings;
    isSelectingFromTable: boolean;
    setIsSelectingFromTable: React.Dispatch<React.SetStateAction<boolean>>;
    selectedMetric: string;
    setSelectedMetric: React.Dispatch<React.SetStateAction<string>>;
    sortingOrder: number;
    setSortingOrder: React.Dispatch<React.SetStateAction<number>>;
    hasCompareDates: boolean;
    showBroadcastData: boolean;
    showInVenueData: boolean;
    currency: string;
    permissions: any;
    platformIds: number[];
    insights: any;
    compareInsights: any;
    dateRange: string;
    compareDateRange: string;
    total: any;
    compareTotal: any;
    values: any[];
    compareValues: any[];
    isLoading: boolean;
    isCompareLoading: boolean;
    groupBy: Record<string, any>;
    groupByList: string[];
    hasTooManyRows: boolean;
    hasTooManyRowsForCompare: boolean;
    data: any[];
    query: any;
    loading: boolean;
    tableSelected: boolean;
    setTableSelected: React.Dispatch<React.SetStateAction<boolean>>;
    yAxisMap: Record<
        string,
        {
            name: string;
            yAxisLabel?: string;
            icon?: React.ReactNode;
            type?: string;
            format?: (value: any) => string;
            chartFormat?: (value: any) => string;
        }
    >;
    isSelectingColumns: boolean;
    setIsSelectingColumns: React.Dispatch<React.SetStateAction<boolean>>;
};

const Provider = ({ children, user, client, filters }: ProviderProps) => {
    const { t } = useTranslation(['common', 'measurement']);
    const location = useLocation();
    const {
        currency: { code: currency },
        permissions,
        platformIds = [],
    } = client;
    const [yAxis, setYAxis] = useState<string>('sponsorMediaValue');
    const [compareMetric, setCompareMetric] = useState('sponsorMediaValue');
    const [page, setPage] = useState(0);
    const [groupByOptions, setGroupByOptions] = useState<any>([]);

    const showBroadcastData = platformIds.includes(BROADCAST);
    const showInVenueData = platformIds.includes(IN_VENUE);

    const insightsColumns = getInsightsColumns({
        showBroadcastData,
        showInVenueData,
    });

    const initialColumns = insightsColumns.reduce((acc, c) => {
        if (!c.dbname) return acc;
        acc[c.dbname] = true;
        return acc;
    }, {});

    const columnSettings = getSettings(user, client, initialColumns);
    const [columns, setColumns] = useState(columnSettings);
    const [tableSelected, setTableSelected] = useState(false);
    const [isSelectingColumns, setIsSelectingColumns] = useState(false);

    const [isSelectingFromTable, setIsSelectingFromTable] = useState(false);
    const [selectedMetric, setSelectedMetric] = useState(
        METRIC_TYPES.sponsorMediaValue,
    );
    const [sortingOrder, setSortingOrder] = useState(SORTING_ORDER.DESC);

    const { rightsholderAttribution } = useRightsholderAttribution({ client });

    const groupBy = useMemo(() => {
        const filtersGroupBy = filters?.insightsGroupBy || [];
        return parseStoredGroupBy(filtersGroupBy);
    }, [filters?.insightsGroupBy]);

    const params = new URLSearchParams(location.search);
    const orderBy = useMemo(() => {
        const orderByUnvalidated = params.getAll('orderBy');
        return orderByUnvalidated.filter(i => orderMap.toReal[i]);
    }, [params]);

    const groupByList = useMemo(
        () => getSelectedGroupByOptions(groupBy),
        [groupBy],
    );

    const hasCompareDates =
        filters.compareDates && !!filters.compareDates.dates;

    const query = useMemo(() => {
        const displayColumns = Object.entries(columns)
            .filter(([, isSelected]) => isSelected)
            .map(([name]) => name);

        if (!displayColumns.includes('postCount')) {
            displayColumns.push('postCount');
        }

        //SC-2036 Quick fix for redirect button (broadcast)
        if (!displayColumns.includes('broadcastCount')) {
            displayColumns.push('broadcastCount');
        }

        if (!displayColumns.includes('inVenueGameCount')) {
            displayColumns.push('inVenueGameCount');
        }

        return {
            groupBy: groupByList,
            orderBy,

            displayColumns,
            includeTextTypeExposures: true,
            ...(rightsholderAttribution === 'league' &&
            permissions?.includes(EDGE_RIGHTSHOLDER_ATTRIBUTION)
                ? { useRightsholderAttribution: true }
                : {}),
        };
    }, [
        groupByList,
        orderBy,
        columns,
        insightsColumns,
        rightsholderAttribution,
    ]);

    // Gate fetches so we never call /media-value-reports with placement/RH ids
    // from the previous attribution mode. When the toggle flips, `query`
    // updates synchronously (useRightsholderAttribution: true/false) but
    // `filters` is rebuilt asynchronously by getFilters() in AppContainer,
    // which translates selected placement/rightsholder ids by name and writes
    // `filters.rightsholderAttributionMode`. Until those match, requests would
    // mix the new query flag with stale ids and 404.
    const expectedAttributionMode =
        permissions?.includes(EDGE_RIGHTSHOLDER_ATTRIBUTION) &&
        rightsholderAttribution === 'league'
            ? 'league'
            : 'team';
    const filtersAttributionMode = filters?.rightsholderAttributionMode;
    // `== null` matches both undefined (pre-mount) and null (the persisted
    // initial value before getFilters has run). Once getFilters writes a
    // concrete 'team' | 'league', the strict comparison gates correctly.
    const isFiltersReady =
        !filters?.isLoading &&
        (filtersAttributionMode == null ||
            filtersAttributionMode === expectedAttributionMode);

    const { data: insights, isLoading } = useInsights({
        filters,
        query,
        client,
        enabled: isFiltersReady,
    });

    const {
        data: compareInsights,
        isLoading: isCompareLoading,
        dateRange,
        compareDateRange,
    } = useCompareInsights({
        filters,
        query,
        metric: compareMetric,
        client,
        enabled: isFiltersReady,
    });

    const { total, values } = useMemo(() => {
        if (!insights)
            return {
                total: undefined,
                values: undefined,
            };
        return insights;
    }, [insights]);

    const { total: compareTotal, values: compareValues = [] } = useMemo(() => {
        if (!compareInsights)
            return {
                total: undefined,
                values: undefined,
            };
        return compareInsights;
    }, [compareInsights]);

    const isFiltersLoading = Boolean(filters?.isLoading);
    // While filters are being realigned to the new attribution mode the
    // queries are disabled and would otherwise report `isLoading: false`,
    // briefly flashing an empty state. Keep the loading state on until
    // filters catch up.
    const isWaitingForFilterSync = !isFiltersReady;
    const isInsightsLoading =
        isFiltersLoading || isWaitingForFilterSync || isLoading;
    const isInsightsCompareLoading =
        isFiltersLoading || isWaitingForFilterSync || isCompareLoading;

    const hasTooManyRows =
        !isInsightsLoading && values && values?.length >= MAX_INSIGHTS_ROWS;
    const hasTooManyRowsForCompare =
        !isInsightsCompareLoading && compareValues?.length >= MAX_INSIGHTS_ROWS;

    useEffect(() => {
        (async () => setGroupByOptions(await getGroupByOptions(t)))();
    }, [t]);

    useEffect(() => {
        if (!groupByList.length) {
            alert('At least one group-by option is required.');
            return;
        }

        if (!Object.values(columns).includes(true)) {
            alert('At least one display column is required.');
            setColumns(initialColumns);
            return;
        }
    }, [groupByList, columns, initialColumns, insights, isLoading]);

    const getData = () => {
        if (hasCompareDates) {
            if (hasTooManyRowsForCompare || isInsightsCompareLoading) return [];
            return compareValues;
        }
        if (hasTooManyRows || isInsightsLoading) return [];
        return values;
    };
    const data = getData();

    const loading = hasCompareDates
        ? isInsightsCompareLoading
        : isInsightsLoading;

    const yAxisMap = useMemo(
        () =>
            getYAxisMap({
                currency,
                t,
            }),
        [currency, t],
    );

    const providerValue = {
        yAxis,
        setYAxis,
        compareMetric,
        setCompareMetric,
        page,
        setPage,
        groupByOptions,
        setGroupByOptions,
        columns,
        setColumns,
        insightsColumns,
        initialColumns,
        isSelectingFromTable,
        setIsSelectingFromTable,
        selectedMetric,
        setSelectedMetric,
        sortingOrder,
        setSortingOrder,
        hasCompareDates,
        showBroadcastData,
        showInVenueData,
        currency,
        permissions,
        platformIds,
        insights,
        compareInsights,
        dateRange,
        compareDateRange,
        total,
        compareTotal,
        values,
        compareValues,
        isLoading: isInsightsLoading,
        isCompareLoading: isInsightsCompareLoading,
        groupBy,
        groupByList,
        hasTooManyRows,
        hasTooManyRowsForCompare,
        data,
        query,
        loading,
        setTableSelected,
        tableSelected,
        yAxisMap,
        isSelectingColumns,
        setIsSelectingColumns,
    };

    return (
        <InsightsContext.Provider value={providerValue}>
            {children}
        </InsightsContext.Provider>
    );
};

export const useInsightsContext = () => {
    const context = React.useContext(InsightsContext);
    if (!context) {
        throw new Error('useInsightsContext must be used within a Provider');
    }
    return context;
};

export default connect(['filters', 'client', 'user'])(Provider);
